import _                                              from 'underscore';
import React, { FC, useContext, useEffect, useState } from 'react';

import { SystemAccountingQueries }                           from '../../../../../queries';
import { CorvalOrganization, CorvalProject, Position, Size } from '../../../../../types';
import { useCurrentUser }                                    from '../../../../../hooks';
import { ProjectDashboardContext }                           from './ProjectDashboardIndexPage';
import { ProjectsByOrganizationListItem }                    from './ProjectsByOrganizationListItem';
import { Cell, Grid }                                        from 'styled-css-grid';
import { EmptyOrganizationList }                             from './EmptyOrganizationList';
import { useQuery }                                          from '@apollo/react-hooks';
import { queriesHaveData, queriesLoading }                   from '../../../../../store/_apolloUtil';
import { StyledContainer }                                   from '../../../../containers';
import { BigGearLoadingMessage }                             from '../../../../components/util/loading/BigGearLoadingMessage';
import { useSystemSettings }                                 from "@src/hooks/useSystemSettings";
import { ProjectQueries }                                    from "@src/queries/ProjectQueries";


interface ProjectsAndOrganizationInfo {
    orgCount: number,
    organization: Partial<CorvalOrganization>,
    projects: CorvalProject[],
}

interface Props {}

const defaultProps: Props = {}

const ProjectsByOrganizationList: FC<Props> = ( props ) => {

    let { currentUser }                             = useCurrentUser(),
        { projectListRequestRef, getVisibleOrgIds } = useContext( ProjectDashboardContext ),
        [ projectsByOrg, setProjectsByOrg ]         = useState<ProjectsAndOrganizationInfo[]>(),
        { filterProjectsByVisibility }              = useSystemSettings();

    const projectListingQueryReq = useQuery( SystemAccountingQueries.queries.GET_PROJECTS(
        `${ SystemAccountingQueries.fragments.PROJECTS.EDIT_FIELDS() }
		organization{ id name }
		isDiscoveryComplete
		createdUtc 
		createdById
		updatedUtc 
		updatedById
		projectVersions { 
		    ${ ProjectQueries.fragments.PROJECTS.VERSION_DETAIL_FIELDS } 
        }`
    ), {
        variables:   {
            where: {
                id:       { in: _.pluck( currentUser.projects, 'projectId' ) },
                isActive: { eq: true },
            },
            order: [ { name: 'ASC' } ]
        },
        fetchPolicy: 'network-only'
    } );

    const isLoading = queriesLoading( projectListingQueryReq );

    useEffect( () => {

        projectListRequestRef.current = projectListingQueryReq;

    }, [] )

    useEffect( () => {

        if( !queriesLoading( projectListingQueryReq ) && queriesHaveData( projectListingQueryReq ) ){

            let { projects } = projectListingQueryReq.data;

            let projectsByOrg = _.chain( projects )
                                 .groupBy( project => project.organization.name )
                                 .pairs()
                                 .sortBy( ( [ orgName, ] ) => orgName )
                                 .map( ( [ , set ] ) => ( {
                                     organization: _.first( set ).organization,
                                     projects:     set,
                                     orgCount:     null,
                                 } ) )
                                 .value();

            projectsByOrg = projectsByOrg.map( projectOrgInfo => ( {
                ...projectOrgInfo,
                orgCount: projectsByOrg.length,
            } ) );

            setProjectsByOrg( projectsByOrg );
        }

    }, [ projectListingQueryReq ] );


    getVisibleOrgIds.current = () => {

        if( _.isArray( projectsByOrg ) ){

            return _.compact( projectsByOrg.map( org => {

                const [ visibleProjects, noVisibleProjects ] = filterProjectsByVisibility( org.projects );

                return ( noVisibleProjects ) ? null : org.organization.id;

            } ) );
        }

        return [];
    }

    return (
        <StyledContainer position={ Position.RELATIVE } padding={ [ null, null, Size.LG, null ] }>

            { isLoading && (

                <StyledContainer padding={ Size.XL }>

                    <BigGearLoadingMessage label={ 'Loading Project Data...' } active={ true }/>

                </StyledContainer>

            ) }

            { projectsByOrg && (
                <>

                    { ( projectsByOrg.length === 0 )
                      ? <EmptyOrganizationList/>
                      : <Grid columns={ 1 } rowGap={ `80px` }>

                          { projectsByOrg.map( ( projectsAndOrg, index ) => {

                              const { orgCount, ...projectsAndOrgData }    = projectsAndOrg,
                                    [ visibleProjects, noVisibleProjects ] = filterProjectsByVisibility( projectsAndOrg.projects );

                              if( noVisibleProjects ) return null;

                              return (

                                  <Cell key={ index }>

                                      <ProjectsByOrganizationListItem
                                          organization={ projectsAndOrgData.organization }
                                          projects={ visibleProjects as CorvalProject[] }
                                      />

                                  </Cell>

                              )

                          } ) }
                      </Grid>
                    }
                </>
            ) }

        </StyledContainer>
    );
};

ProjectsByOrganizationList.defaultProps = defaultProps;

export { ProjectsByOrganizationList }
